import { Component, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { captureException } from '@/instrument';

interface Props {
  fallback?: (err: Error) => ReactNode;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Local error boundary for screens that consume large, externally-provided
 * payloads (sourcing snapshots, saved searches…) where a single malformed
 * entry shouldn't blank the entire page. Logs to the console so the bug
 * stays visible during development.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, info.componentStack);
    // Forward to Sentry — no-op unless VITE_SENTRY_DSN is configured.
    captureException(error, { react: { componentStack: info.componentStack } });
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error);
      return (
        <div className="rounded-lg border border-danger/30 bg-danger-soft/40 p-4 text-sm">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
            <div className="flex-1">
              <div className="font-semibold text-danger">
                Affichage impossible
              </div>
              <div className="mt-0.5 text-xs text-danger/80">
                {this.state.error.message}
              </div>
              <button
                type="button"
                onClick={this.reset}
                className="mt-2 text-xs font-medium text-accent hover:underline"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
